<script setup lang="ts">
import { nextTick, onMounted, provide, ref, watch } from 'vue';
import DncCsvUpload from '@/dnc/components/DncCsvUpload/DncCsvUpload.vue';
import { TRIGGER_FORM_VALIDATION } from '@/app/utils/keys';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { DATA_QA } from './dnc-import-upload-step.constants';
import { usePhoneCsvStore } from '@/stores';
import type { PhoneCsv } from '@/apis/generated/dialbunny';
import { ResponseError } from '@/apis/generated/common/runtime';
import { instanceOfPhoneCsvPostAssertErrorResponse } from '@/apis/generated/dialbunny';
import logger from '@/logger';

enum DncCsvUploadClientError {
  REQUIRED = 'REQUIRED',
  INVALID_FILE_TYPE = 'INVALID_FILE_TYPE',
  PARSING_ERROR = 'PARSING_ERROR',
  DUPLICATE_HEADERS = 'DUPLICATE_HEADERS',
  NO_PHONE_COLUMN = 'NO_PHONE_COLUMN',
  INVALID_PHONE_FORMAT = 'INVALID_PHONE_FORMAT',
  UNKNOWN = 'UNKNOWN',
}

const log = logger.getChild('DncImportUploadStep');

log.debug('Mounting');

onMounted(() => {
  log.debug('Mounted');
});

const dncCsv = defineModel<PhoneCsv | null>({
  required: true,
});

const emit = defineEmits(['continue', 'update:examples']);

const validationTrigger = ref(0);
provide(TRIGGER_FORM_VALIDATION, validationTrigger);

const file = ref<File | null>(null);
const loading = ref(false);
const errors = ref(new Map<string, string | null>());

const dncCsvUploadRef = ref<InstanceType<typeof DncCsvUpload> | null>(null);
const phoneCsvStore = usePhoneCsvStore();

const setError = (key: string, error: string | null) => {
  log.debug('Setting error', key, error);
  errors.value.set(key, error);
};

const validate = async (): Promise<boolean> => {
  log.debug('Validating...');
  validationTrigger.value++;

  await nextTick();

  const isValid = [...errors.value.values()].every((error) => error === null);

  log.debug('Validation result:', isValid);
  return isValid;
};

const mapErrorTypeToFrontendError = (
  errorType: string,
): DncCsvUploadClientError => {
  switch (errorType) {
    case 'phone_column_required':
      return DncCsvUploadClientError.NO_PHONE_COLUMN;
    case 'csv_empty':
      return DncCsvUploadClientError.PARSING_ERROR;
    default:
      return DncCsvUploadClientError.UNKNOWN;
  }
};

const handleCreateError = async (error: unknown) => {
  log.debug('Create error:', error);

  if (!(error instanceof ResponseError)) {
    log.error(
      'Unexpected error type:',
      error instanceof Error ? error.message : error,
    );
    setError('file', 'UPLOAD_FAILED');
    return;
  }

  try {
    const json = await error.response.json();
    log.debug('Error response:', json);

    if (!instanceOfPhoneCsvPostAssertErrorResponse(json)) {
      log.error('Unexpected error type in response:', json);
      setError('file', 'UPLOAD_FAILED');
      return;
    }

    const frontendErrorType = mapErrorTypeToFrontendError(json.type);
    dncCsvUploadRef.value?.setError(frontendErrorType);
  } catch (jsonError) {
    log.error('Failed to parse error response:', jsonError);
    setError('file', 'UPLOAD_FAILED');
  }
};

const submit = async () => {
  log.debug('Submitting');
  const csvToUpload = file.value;
  if (!(await validate()) || !csvToUpload) {
    return;
  }

  if (!dncCsv.value) {
    loading.value = true;
    log.debug('Creating phone CSV record...');

    phoneCsvStore.create(
      csvToUpload,
      {
        onSuccess: (created) => {
          dncCsv.value = created;
          log.debug('Create successful', created);
          loading.value = false;
          emit('continue');
        },
        onRetry: (error) => {
          log.warn('Retrying phone CSV creation due to error:', error);
        },
        onError: async (error) => {
          log.error(
            'Phone CSV creation failed with unrecoverable error:',
            error,
          );
          await handleCreateError(error);
          loading.value = false;
        },
      },
      false,
    );
  } else {
    emit('continue');
  }
};

watch([file], () => {
  dncCsv.value = null;
});
</script>
<template>
  <div :data-qa="DATA_QA.ROOT">
    <DncCsvUpload
      ref="dncCsvUploadRef"
      v-model="file"
      required
      :processing="loading"
      @update:examples="($event) => emit('update:examples', $event)"
      @update:error="($event) => setError('file', $event)"
    />
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton :data-qa="DATA_QA.CONTINUE" :loading="loading" @click="submit">
        {{ $t('DNC_IMPORT_CONTINUE') }}
      </DtButton>
    </div>
  </div>
</template>
